import React from 'react';
import { Expense, ExpenseCategory } from '../../types';
import { EXPENSE_CATEGORIES, normalizeDate } from '../../utils';
import { useChart } from './useChart';
import { categoryName } from './cateogryName';

interface BarProps {
  expenses: Expense[];
  begin: string;
  end: string;
  freq: 'daily' | 'weekly' | 'monthly';
}

type ExpenseByDate = {
  date: Date,
  amount: number,
}

type Data = Record<ExpenseCategory, Array<ExpenseByDate>>;

const getDateRange = (begin: Date, end: Date, freq: 'daily' | 'weekly' | 'monthly'): Date[] => {
  const dates: Date[] = [];
  let date = new Date(begin);

  if (freq === 'daily') {
    while (date <= end) {
      dates.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
  }
  return dates;
}

const constructData = (expenses: Expense[], dates: Date[]): Data => {
  const data = {} as Data;

  EXPENSE_CATEGORIES.forEach((category) => {
    data[category] = [];
  });

  dates.forEach((date) => {
    EXPENSE_CATEGORIES.forEach((category) => {
      data[category].push({
        date,
        amount: 0,
      });
    });
  });

  if (expenses.length === 0) {
    return data;
  }

  let iDates = 0;
  let iExpenses = 0;

  expenses.sort((a, b) => {
    return a.date.getTime() - b.date.getTime();
  });

  while (iDates < dates.length && iExpenses < expenses.length) {
    const currentDate = dates[iDates];
    const currentExpense = expenses[iExpenses];
    const expenseDate = new Date(normalizeDate(currentExpense.date));

    if (currentDate.getTime() < expenseDate.getTime()) {
      iDates++;
    } else {
      data[currentExpense.category][iDates].amount += currentExpense.amount;
      iExpenses++;
    }
  }

  return data;
}

export default function Bar({expenses, begin, end, freq}: BarProps) {
  const chartRef = React.useRef<HTMLDivElement>(null);
  const chart = useChart(chartRef);

  const beginDate = React.useMemo(() => new Date(begin), [begin]);
  const endDate = React.useMemo(() => new Date(end), [end]);

  const dates = React.useMemo(() => getDateRange(beginDate, endDate, freq), [beginDate, endDate, freq]);
  const data = React.useMemo(() => constructData(expenses, dates), [expenses, dates]);

  const series = React.useMemo(() => EXPENSE_CATEGORIES.map((category) => ({
    name: categoryName(category),
    type: 'bar',
    stack: 'Total',
    emphasis: {
      focus: 'series'
    },
    data: data[category].map((expense) => expense.amount)
  })), [data]);

  const option = React.useMemo(() => ({
    legend: {},
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: [
      {
        type: 'category',
        data: dates.map((date) => normalizeDate(date))
      }
    ],
    yAxis: [
      {
        type: 'value'
      }
    ],
    series
  }), [series, dates]);

  React.useEffect(() => {
    if (!chart) {
      return;
    }
    chart.setOption(option);
  }, [chart, option, chartRef]);

  return (
    <div ref={chartRef} style={{ width: '100%', height: 400, marginTop: 100 }} />
  )
}
